import { Capacitor } from '@capacitor/core';
import { Box } from '@deckee/deck-hand';
import classNames from 'classnames/bind';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../services/event-capture';
import { selectShowingMenuBar } from '../../../services/store/modules/app-controls';
import { selectCurrentMember } from '../../../services/store/modules/backend-service';
import {
  MenuItemEnum,
  useActiveMenu,
  useNavigateToMenu,
} from '../../features/AppNavigation/useAppNavigation';
import { MenuBar, MenuBarItem } from '../../shared/MenuBar/MenuBar';
import MemberAvatar from '../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import {
  checklistFilled,
  checklistOutlined,
  customer,
  customerOutlined,
  locationOutlined,
  locationOutlinedFilled,
  track,
  trackFilled,
  weather,
  weatherFilled,
} from '../../shared/vector-images';
import styles from './AppControls.scss';

const cx = classNames.bind(styles);

const ConstructMenuBar = (
  activeMenu: string,
  member: { id: any; image: any },
) => {
  const navigateToMenu = useNavigateToMenu();
  const theme = useTheme();

  const { image } = member || {};

  const handleMenuBarItemClick = (menuName: MenuItemEnum) => () => {
    captureEvent({
      type: 'menu-bar: select item',
      payload: {
        name: menuName,
      },
      sendToAmplitude: true,
    });

    navigateToMenu(menuName);
  };

  return (
    // Be mindful that if the MenuBarItem active prop is changed then this will affect
    // the way the active menu item is set below
    <MenuBar className={cx('app-controls__menu-bar')}>
      <MenuBarItem
        key={MenuItemEnum.PLAN}
        active={activeMenu === MenuItemEnum.PLAN}
        label="Explore"
        action={handleMenuBarItemClick(MenuItemEnum.PLAN)}
        // Likely to include a filled version, so leave condition for now
        icon={
          activeMenu === MenuItemEnum.PLAN
            ? locationOutlinedFilled()
            : locationOutlined()
        }
      />
      <MenuBarItem
        key={MenuItemEnum.PREPARE}
        active={activeMenu === MenuItemEnum.PREPARE}
        label="Prepare"
        action={handleMenuBarItemClick(MenuItemEnum.PREPARE)}
        icon={
          activeMenu === MenuItemEnum.PREPARE
            ? checklistFilled()
            : checklistOutlined()
        }
      />
      <MenuBarItem
        key={MenuItemEnum.WEATHER}
        active={activeMenu === MenuItemEnum.WEATHER}
        label="Weather"
        action={handleMenuBarItemClick(MenuItemEnum.WEATHER)}
        icon={activeMenu === MenuItemEnum.WEATHER ? weatherFilled() : weather()}
      />
      {Capacitor.isNativePlatform() && (
        <MenuBarItem
          key={MenuItemEnum.TRACK}
          active={activeMenu === MenuItemEnum.TRACK}
          label="Track"
          action={handleMenuBarItemClick(MenuItemEnum.TRACK)}
          icon={activeMenu === MenuItemEnum.TRACK ? trackFilled() : track()}
        />
      )}
      <MenuBarItem
        key={MenuItemEnum.YOU}
        active={activeMenu === MenuItemEnum.YOU}
        label="You"
        action={handleMenuBarItemClick(MenuItemEnum.YOU)}
        // Without a photo, children must be undefined so MenuBarItem renders the icon
        icon={activeMenu === MenuItemEnum.YOU ? customer() : customerOutlined()}
      >
        {image ? (
          <Box
            mt="2px"
            mb="4px"
            borderRadius="50%"
            border="2px solid"
            borderColor={
              activeMenu === MenuItemEnum.YOU
                ? theme.colors.primary
                : 'transparent'
            }
          >
            <MemberAvatar
              memberAvatarId={image}
              width={20}
              height={20}
              addBorder={false}
              applyElevation={false}
            />
          </Box>
        ) : null}
      </MenuBarItem>
    </MenuBar>
  );
};

const ConnectedMenuBar = () => {
  const showingMenuBar = useSelector(selectShowingMenuBar);
  const member = useSelector(selectCurrentMember);

  const currentActiveMenu = useActiveMenu();

  return showingMenuBar && ConstructMenuBar(currentActiveMenu, member);
};

export default ConnectedMenuBar;
